// src/pages/DisputesPage.js
import React, { useState, useEffect } from "react";
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebase";
import { formatDateTime } from "../utils/dateFormat";

const TYPE_META = {
  no_show:        { label: "No-show",            color: "#EF4444", icon: "🚫" },
  late_cancel:    { label: "Late cancellation",  color: "#EAB308", icon: "⏰" },
  did_not_happen: { label: "Job not completed",  color: "#EF4444", icon: "❌" },
};
const STATUS_META = {
  open:      { label: "Awaiting driver (48h)", color: "#EAB308" },
  disputed:  { label: "Disputed — needs review", color: "#3B82F6" },
  upheld:    { label: "Upheld", color: "#EF4444" },
  dismissed: { label: "Dismissed", color: "#22C55E" },
};

export default function DisputesPage() {
  const [disputes, setDisputes] = useState([]);
  const [tab, setTab] = useState("disputed"); // default: the ones needing admin action
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "disputes"), orderBy("reportedAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setDisputes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => console.error("disputes listener:", err));
    return unsub;
  }, []);

  const resolve = async (dispute, outcome) => {
    const note = window.prompt(
      outcome === "upheld"
        ? "Optional note — why is the report upheld?"
        : "Optional note — why is the dispute dismissed (driver cleared)?",
      ""
    );
    if (note === null) return; // cancelled
    setProcessing(true);
    try {
      await addDoc(collection(db, "disputeResolutions"), {
        disputeId: dispute.id,
        outcome,
        note: note || null,
        adminId: getAuth().currentUser?.uid || "admin",
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("resolve dispute:", e);
      alert("Failed to submit resolution. Try again.");
    } finally {
      setProcessing(false);
    }
  };

  const counts = {
    disputed:  disputes.filter((d) => d.status === "disputed").length,
    open:      disputes.filter((d) => d.status === "open").length,
    resolved:  disputes.filter((d) => d.status === "upheld" || d.status === "dismissed").length,
  };
  const filtered = disputes.filter((d) =>
    tab === "resolved" ? (d.status === "upheld" || d.status === "dismissed") : d.status === tab
  );

  const TABS = [
    { key: "disputed", label: `Needs Review (${counts.disputed})` },
    { key: "open",     label: `Awaiting Driver (${counts.open})` },
    { key: "resolved", label: `Resolved (${counts.resolved})` },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto" }}>
      <h1 style={{ color: "var(--text)", fontSize: 24, fontWeight: 800, marginBottom: 4 }}>⚖️ Disputes</h1>
      <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 20 }}>
        Driver disputes of no-shows, late cancellations, and incomplete jobs. Upholding applies the reliability penalty; dismissing clears the driver.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ background: tab === t.key ? "rgba(249,115,22,0.15)" : "var(--surface2)", border: `1px solid ${tab === t.key ? "rgba(249,115,22,0.4)" : "var(--border)"}`, color: tab === t.key ? "#F97316" : "var(--text-secondary)", borderRadius: 9, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            {t.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ color: "var(--text-muted)", textAlign: "center", padding: 48, fontSize: 14 }}>No disputes in this view.</div>
      ) : (
        filtered.map((d) => {
          const tm = TYPE_META[d.type] || { label: d.type, color: "#6B7280", icon: "•" };
          const sm = STATUS_META[d.status] || { label: d.status, color: "#6B7280" };
          return (
            <div key={d.id} style={{ background: "var(--surface)", border: "1px solid var(--surface2)", borderRadius: 14, padding: 18, marginBottom: 14, borderLeft: `3px solid ${tm.color}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
                    <span style={{ color: "var(--text)", fontWeight: 700, fontSize: 16 }}>{tm.icon} {tm.label}</span>
                    <span style={{ background: sm.color + "20", color: sm.color, borderRadius: 20, padding: "2px 11px", fontSize: 11.5, fontWeight: 600 }}>{sm.label}</span>
                  </div>
                  <div style={{ color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.7 }}>
                    <div>Job: <strong style={{ color: "var(--text)" }}>{d.jobTitle || "—"}</strong></div>
                    <div>Driver: <strong style={{ color: "var(--text)" }}>{d.driverName || d.driverId}</strong></div>
                    <div>Reported: {formatDateTime(d.reportedAt)}</div>
                    {d.pendingPenalty != null && <div>Penalty if upheld: <strong style={{ color: "#EAB308" }}>{d.pendingPenalty} pts</strong></div>}
                  </div>
                </div>
              </div>

              {d.driverResponse && (
                <div style={{ marginTop: 12, background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ color: "#3B82F6", fontSize: 11, fontWeight: 700, marginBottom: 4 }}>DRIVER'S RESPONSE</div>
                  <div style={{ color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.6 }}>{d.driverResponse}</div>
                </div>
              )}

              {d.status === "resolved" || d.status === "upheld" || d.status === "dismissed" ? (
                d.outcomeNote && <div style={{ marginTop: 10, color: "var(--text-muted)", fontSize: 12, fontStyle: "italic" }}>Note: {d.outcomeNote}</div>
              ) : (
                <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                  <button onClick={() => resolve(d, "upheld")} disabled={processing}
                    style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.35)", color: "#EF4444", borderRadius: 9, padding: "8px 15px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                    Uphold report (apply penalty)
                  </button>
                  <button onClick={() => resolve(d, "dismissed")} disabled={processing}
                    style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.35)", color: "#22C55E", borderRadius: 9, padding: "8px 15px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                    Dismiss (clear driver)
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}